import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createAutomation,
    deleteAutomation,
    publishAutomation,
    unpublishAutomation,
    updateAutomation,
} from '@/lib/api/automations';
import { QUERY_KEYS } from '@/lib';
import type { Automation, AutomationFormData } from '@/features/automations/types/automation.types';

/**
 * Create a new automation
 */
export function useCreateAutomation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: AutomationFormData) => createAutomation(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATIONS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATION_ANALYTICS] });
        },
        onError: error => {
            console.error('Error creating automation:', error);
        },
    });
}

/**
 * Update an existing automation
 * Note: Notifications are handled by the calling hook (useAutomationFormActions)
 */
export function useUpdateAutomation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: AutomationFormData }) => updateAutomation(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATIONS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATION_DETAILS, variables.id] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATION_ANALYTICS] });
        },
        onError: error => {
            console.error('Error updating automation:', error);
        },
    });
}

/**
 * Delete an automation (soft delete - sets status to 'deleted')
 * Note: Notifications are handled by the calling hook (useAutomationActions)
 * Note: Optimistic updates are disabled to allow for delete animation to play
 */
export function useDeleteAutomation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => deleteAutomation(id),
        onSuccess: () => {
            // Invalidate queries after successful deletion
            // This will refetch the data and remove the deleted item
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATIONS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATION_ANALYTICS] });
        },
        onError: error => {
            console.error('Error deleting automation:', error);
        },
    });
}

/**
 * Publish an automation (change status to 'published')
 * Note: Notifications are handled by the calling hook (useAutomationActions)
 */
export function usePublishAutomation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, automation }: { id: number; automation: Automation }) => publishAutomation(id, automation),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATIONS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATION_ANALYTICS] });
        },
        onError: error => {
            console.error('Error publishing automation:', error);
        },
    });
}

/**
 * Unpublish an automation (change status to 'draft')
 * Note: Notifications are handled by the calling hook (useAutomationActions)
 */
export function useUnpublishAutomation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, automation }: { id: number; automation: Automation }) => unpublishAutomation(id, automation),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATIONS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUTOMATION_ANALYTICS] });
        },
        onError: error => {
            console.error('Error unpublishing automation:', error);
        },
    });
}
